'use client'

import { useEffect, useRef, useState } from 'react'

import { PhnxIFrame } from '../../01-atoms/IFrame/IFrame'
import styles from './Brucebanner.module.css'

export type BrucebannerProps = {
  title?: string
  subtitle?: string
  description?: string
  asset?: { src: string; alt: string; focus?: string; washOpacity: number }
  frameSrc?: any
  customForm?: any
  heroForm?: any
  minHeight?: string
  mobileMinHeight?: string
  content?: React.ReactNode
  variant?: 'default' | 'feature'
}

interface Entry {
  geo: string
  town: string
}

export const PhnxBrucebanner: React.FC<BrucebannerProps> = ({
  title,
  subtitle,
  description,
  asset,
  frameSrc,
  heroForm,
  minHeight = '910px',
  mobileMinHeight = '1050px',
  customForm,
  content,
  variant = 'default',
}) => {
  const [hasMounted, setHasMounted] = useState(false)
  const [isDesktop, setIsDesktop] = useState(false)
  const [imageUri, setImageUri] = useState<string>('')
  const [keyword, setKeyword] = useState<string>('Wealth Management')
  const [geoValue, setGeoValue] = useState<string | null>(null)
  const [hasNearMe, setHasNearMe] = useState<boolean>(false)
  const [town, setTown] = useState<string>('')
  const [data, setData] = useState<Entry[]>([])

  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)

  const hasForm = !frameSrc && customForm === undefined && heroForm?.length === 0 ? false : true
  const wash_opacity = asset?.washOpacity || 0

  useEffect(() => {
    setHasMounted(true)

    const updateIsDesktop = () => {
      setIsDesktop(window.matchMedia('(min-width: 768px)').matches)
    }

    // Set initial value
    updateIsDesktop()

    // Add resize listener
    window.addEventListener('resize', updateIsDesktop)

    // Cleanup
    return () => window.removeEventListener('resize', updateIsDesktop)
  }, [])

  useEffect(() => {
    if (!hasMounted) return

    const canvas = canvasRef.current
    const container = containerRef.current
    if (canvas && container) {
      const context = canvas.getContext('2d')
      if (context) {
        const col = (x: number, y: number, r: number, g: number, b: number) => {
          context.fillStyle = `rgb(${r}, ${g}, ${b})`
          context.fillRect(x, y, 1, 1)
        }
        const R = (x: number, y: number, t: number) =>
          Math.floor(192 + 64 * Math.cos((x * x - y * y) / 300 + t))
        const G = (x: number, y: number, t: number) =>
          Math.floor(192 + 64 * Math.sin((x * x * Math.cos(t / 4) + y * y * Math.sin(t / 3)) / 300))
        const B = (x: number, y: number, t: number) =>
          Math.floor(
            192 + 64 * Math.sin(5 * Math.sin(t / 9) + ((x - 100) ** 2 + (y - 100) ** 2) / 1100)
          )
        let t = 0
        const run = () => {
          for (let x = 0; x <= 35; x++) {
            for (let y = 0; y <= 35; y++) {
              col(x, y, R(x, y, t), G(x, y, t), B(x, y, t))
            }
          }
          t += 0.02
          window.requestAnimationFrame(run)
        }
        run()
      }
    }
  }, [hasMounted])

  useEffect(() => {
    if (!hasMounted) return
    const urlParams = new URLSearchParams(window.location.search)
    const h = urlParams.get('h')
    const geo = urlParams.get('geo')
    setGeoValue(geo)
    switch (h) {
      case '1':
        setKeyword('Financial Advisor')
        break
      case '2':
        setKeyword('Investment')
        break
      case '3':
        setKeyword('Retirement')
        break
      case '4':
        setKeyword('Superannuation')
        break
      default:
        setKeyword('Wealth Management')
    }
  }, [hasMounted])

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch('/json/locations.json')
        const jsonData = await response.json()
        setData(jsonData)
      } catch (e) {
        console.error('Failed to load location data:', e)
      }
    }
    fetchData()
  }, [])

  useEffect(() => {
    if (data.length > 0 && geoValue) {
      setHasNearMe(true)
      const geoValueAsNumber = Number(geoValue)
      const item = data.find((entry) => Number(entry.geo) === geoValueAsNumber)
      setTown(item ? 'in ' + item.town : 'in Australia')
    }
  }, [data, geoValue])

  useEffect(() => {
    if (asset?.src) {
      const imageHeightWidth = !hasForm ? '2880x800' : isDesktop ? '2880x1350' : '1440x675'
      let uri = `${asset.src}/m/${imageHeightWidth}/filters:quality(80)`
      if (asset.focus) uri += `:focal(${asset.focus})`
      setImageUri(uri)
    }
  }, [asset, isDesktop])

  if (!hasMounted) return null

  return (
    <div
      className={`${styles['brucebanner']} ${styles[variant === 'feature' ? 'feature' : '']}`}
      style={{
        borderImage: `linear-gradient(hsl(0 0% 0% / ${wash_opacity}), hsl(0 0% 0% /${wash_opacity})) fill 1`,
        ...(variant !== 'feature' && { backgroundImage: `url("${imageUri}")` }),
      }}
      data-testid="brucebanner"
      ref={containerRef}
    >
      {variant === 'feature' && <canvas id="canv" width="32" height="32" ref={canvasRef} />}
      {variant === 'feature' && (
        <img
          src={`/assets/${isDesktop ? 'mask2.webp' : 'mask_mobile2.webp'}`}
          className={styles['featuremask']}
          alt=""
        />
      )}
      <div className={`${styles['bruce-container']} container ${styles[variant]}`}>
        <div
          className={`${styles['bruce-col']} ${!hasForm ? styles['centerTitle'] : ''} ${styles[variant === 'feature' ? 'text-feature' : '']}`}
        >
          {variant === 'feature' && (
            <img
              src="/assets/Findex_Logo_White_Dash.svg"
              alt="Findex Logo"
              width={135}
              height={44}
              className={styles.logo}
            />
          )}
          {variant !== 'feature' && subtitle && <h5 className={styles.title}>{subtitle}</h5>}
          {variant !== 'feature' && title && <h1 className={styles['bruce-subtitle']}>{title}</h1>}
          {keyword && variant === 'feature' && title && (
            <h1 className={styles['bruce-subtitle']}>
              {title} {hasNearMe && town}
            </h1>
          )}
          {variant === 'feature' && subtitle && <h5 className={styles.title}>{subtitle}</h5>}
          {description && <p>{description}</p>}
          {content && <div className={styles['bruce-content']}>{content}</div>}
        </div>

        {(frameSrc || customForm || (heroForm && heroForm.length > 0)) && (
          <div
            className={`${styles['bruce-col']} ${styles[variant === 'feature' ? 'col-feature' : '']}`}
          >
            {frameSrc && (
              <PhnxIFrame
                src={frameSrc}
                frameHeight={minHeight}
                mobileFrameHeight={mobileMinHeight}
              />
            )}
            {heroForm && !frameSrc && heroForm}
            {customForm && !frameSrc && customForm}
          </div>
        )}
      </div>
    </div>
  )
}
